import {
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Affix,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
} from "antd";
import CustomDatePicker from "common/CustomDatePicker";
import ChallanForm from "components/Configuration/ChallanConfig/ChallanForm";
import ShipmentTransportCharges, {
  transportChargeFormValues,
  transportChargePayload,
} from "components/Account/ShipmentTransportCharges";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ACCEPTANCE_B2BLC_LIST,
  B2BLC_INVOICE_LIST,
  CHALLAN_LIST,
  EXPORT_SHIPMENT,
  EXPORT_SHIPMENT_FOC_INVOICE_LIST,
  EXPORT_SHIPMENT_LC_INVOICE_LIST,
  FOC_TYPE_INVOICE_LIST,
  GET_BUYER_ENDPOINT,
  GET_BUYING_OFFICE_ENDPOINT,
  IMPORT_VEHICLE,
  SALES_CONTRACT_LIST,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData, putData } from "../../../apiServices/common";
import HandleNotification from "../../../common/Notification";
import { isArrayAndHasValue } from "../../../utils/functions";
import Header from "../../Common/Header";

// Constants
const transporter = [
  { id: 1, value: "Self Transport" },
  { id: 2, value: "Raj Transport" },
  { id: 3, value: "SR Enterprise" },
  { id: 4, value: "Vendor" },
];

const importer_names = [
  { id: 1, value: "CONCORDE GARMENTS LTD." },
  { id: 2, value: "OSMAN INTERLININGS LTD." },
  { id: 2, value: "OSMAN ACCESSORIES" },
  { id: 2, value: "OTL-DOUBLE GULL MFG. CO. LTD" },
  { id: 2, value: "LILY APPARELS LTD" },
  { id: 2, value: "SSL WIRELESS" },
  { id: 2, value: "REFLECTIONS" },
  { id: 2, value: "STAINED GLASS OVERLAY" },
  { id: 2, value: "BIDDYUT LTD." },
];

const place_of_loading = [
  { id: 1, value: "CTG" },
  { id: 2, value: "AIRPORT" },
  { id: 3, value: "DEPZ" },
  { id: 4, value: "AEPZ" },
  { id: 5, value: "COMILLA EPZ" },
  { id: 6, value: "BENAPOL" },
  { id: 7, value: "ICD" },
  { id: 8, value: "KAMALAPUR" },
  { id: 9, value: "OTHERS" },
];

const place_of_arrival = [
  { id: 1, value: "MIRPUR" },
  { id: 1, value: "SAVAR EPZ" },
  { id: 1, value: "ASHULIA" },
  { id: 1, value: "ESKATON" },
  { id: 1, value: "KAWRANBAZAR" },
  { id: 1, value: "BARIDHARA" },
  { id: 1, value: "OTHERS" },
];

const import_vehicle_types = [
  "1 Ton",
  "2 Ton",
  "3 Ton",
  "4 Ton",
  "5 Ton",
  "6 Ton",
  "7 Ton",
  "8 Ton",
  "9 Ton",
  "10 Ton",
  "11 Ton",
  "12 Ton",
];

const GoodsDeliveryForm = forwardRef((props, ref) => {
  // Props
  const { editMode } = props;

  // States
  const [b2bLcList, setB2bLcList] = useState([]);
  const [poDetailsData, setPoDetailsData] = useState([{ unique_id: 0 }]);
  const [challanList, setChallanList] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [salesContractList, setSalesContractList] = useState(null);
  const [updatePerformed, setUpdatePerformed] = useState(false);
  const [vehicleTypeList, setVehicleTypeList] = useState(import_vehicle_types);
  const [savedImporterName, setSavedImporterName] = useState(null);
  const [buyerList, setBuyerList] = useState([]);
  const [buyingOfficeList, setBuyingOfficeList] = useState([]);

  // Router
  const navigate = useNavigate();
  const { importId } = useParams();
  const location = useLocation();
  const isExport = location?.pathname?.startsWith("/export/");
  const shipmentLabel = isExport ? "Export" : "Import";
  const listPath = isExport ? "/export/goods-delivery" : "/import/goods-delivery";
  const loadingPlaceOptions = isExport ? place_of_arrival : place_of_loading;
  const arrivalPlaceOptions = isExport ? place_of_loading : place_of_arrival;
  const companyNames = importer_names.map((option) => option.value);
  const importerNameOptions =
    savedImporterName && !companyNames.includes(savedImporterName)
      ? [...companyNames, savedImporterName]
      : companyNames;
  const shipmentApi = isExport ? EXPORT_SHIPMENT : IMPORT_VEHICLE;
  const lcInvoiceListApi = isExport
    ? EXPORT_SHIPMENT_LC_INVOICE_LIST
    : B2BLC_INVOICE_LIST;
  const focInvoiceListApi = isExport
    ? EXPORT_SHIPMENT_FOC_INVOICE_LIST
    : FOC_TYPE_INVOICE_LIST;

  // Antd
  const [form] = Form.useForm();
  const { confirm } = Modal;
  const { Option } = Select;

  const getSelectedVehicleStatus = useCallback(
    async (id) => {
      const query = `${shipmentApi}/${id}`;
      const response = await getData(query);

      if (response && response?.data?.code === 200) {
        const masterData = response?.data?.data;

        // Ensure challan is in the list if it exists
        if (masterData?.challan_id) {
          const getChallanQuery = `${CHALLAN_LIST}?id=${masterData?.challan_id}`;
          const challanResponse = await getData(getChallanQuery);
          if (challanResponse && challanResponse?.data?.code === 200) {
            const challanData = challanResponse.data.data?.data || [];
            setChallanList(challanData);
          }
        }

        form.setFieldsValue({
          vehicle_number: masterData?.vehicle_number,
          transporter: masterData?.transporter,
          vehicle_size: masterData?.vehicle_size,
          vehicle_type: masterData?.vehicle_type,
          vehicle_qty: masterData?.vehicle_qty,
          empty_vehicle_arrival_time: masterData?.empty_vehicle_arrival_time
            ? moment(masterData?.empty_vehicle_arrival_time)
            : null,
          driver_name: masterData?.driver_name,
          driver_cell_number: masterData?.driver_cell_number,
          driving_license_number: masterData?.driving_license_number,
          driver_nid_number: masterData?.driver_nid_number,
          loading_place: masterData?.loading_place,
          dispatch_time: masterData?.dispatch_time
            ? moment(masterData?.dispatch_time)
            : null,
          challan_id: masterData?.challan_id,
          gate_pass_no: masterData?.gate_pass_no,
          security_bolt_seal_number: masterData?.security_bolt_seal_number,
          gprs_tracking_number: masterData?.gprs_tracking_number,
          delivery_place: masterData?.delivery_place,
          unloading_date: masterData?.unloading_date
            ? moment(masterData?.unloading_date)
            : null,
          arrival_date: masterData?.arrival_date
            ? moment(masterData?.arrival_date)
            : null,
          importer_name: masterData?.importer_name,
          shipment_by: masterData?.shipment_by,
          ...transportChargeFormValues(masterData),
        });
        setSavedImporterName(masterData?.importer_name || null);

        const poData =
          isArrayAndHasValue(masterData?.import_vehicle_details) &&
          masterData?.import_vehicle_details?.map((item, index) => {
            return {
              unique_id: index,
              b2b_lc_id: item?.b2b_lc_id ? Number(item?.b2b_lc_id) : null,
              import_details_id: item?.import_details_id
                ? Number(item?.import_details_id)
                : null,
              loading_qty: item?.loading_qty ? Number(item?.loading_qty) : 0,
              loading_cbm: item?.loading_cbm ? Number(item?.loading_cbm) : 0,
              gross_weight: item?.invoice_info?.gross_weight
                ? Number(item?.invoice_info?.gross_weight)
                : 0,
              cbm: item?.invoice_info?.cbm
                ? Number(item?.invoice_info?.cbm)
                : 0,
              import_qty: item?.invoice_info?.quantity_ctn
                ? Number(item?.invoice_info?.quantity_ctn)
                : 0,
              invoice_list: item?.b2b_lc_info?.invoice_list,
              type_of_shipment: item?.type_of_shipment,
              export_carton: item?.export_carton,
              garment_piece_qty: item?.garment_piece_qty,
              buyer_id: item?.buyer_id ? Number(item.buyer_id) : null,
              buying_office_id: item?.buying_office_id
                ? Number(item.buying_office_id)
                : null,
              buyer_info: item?.buyer_info || null,
              buying_office_info: item?.buying_office_info || null,
              sales_contract_id: item?.sales_contract_id,
              sales_contract_info: item?.sales_contract_info || null,
            };
          });

        setPoDetailsData(poData);
        if (isExport && Array.isArray(poData)) {
          setBuyerList((prev) => {
            const extras = poData
              .map((item) => item.buyer_info)
              .filter((item) => item?.id);
            const ids = new Set(prev.map((item) => item.id));
            return [
              ...prev,
              ...extras.filter((item) => !ids.has(item.id)),
            ];
          });
          setBuyingOfficeList((prev) => {
            const extras = poData
              .map((item) => item.buying_office_info)
              .filter((item) => item?.id);
            const ids = new Set(prev.map((item) => item.id));
            return [
              ...prev,
              ...extras.filter((item) => !ids.has(item.id)),
            ];
          });
        }
      } else {
        HandleNotification(
          "error",
          "bottomRight",
          "Something Went Wrong!",
          null,
        );
      }
    },
    [form, isExport, shipmentApi],
  );

  useEffect(() => {
    if (editMode && importId) {
      getSelectedVehicleStatus(importId);
    }
  }, [editMode, importId, getSelectedVehicleStatus]);

  const onChangePoDetailsValue = (value, index, keyName) => {
    // Copy poDetailsData state array
    const poDetailsDataCopy = structuredClone(poDetailsData);
    const foundPoItem = poDetailsDataCopy?.find(
      (item) => item?.unique_id === index,
    );
    foundPoItem[keyName] = value;

    setPoDetailsData(poDetailsDataCopy);
  };

  const getB2BLcList = async () => {
    const query = `${ACCEPTANCE_B2BLC_LIST}`;
    const response = await getData(query);

    if (response && response?.data?.code === 200) {
      setB2bLcList(response?.data?.data || []);
    }
  };

  const getAllChallan = async (filterValues) => {
    const { name } = filterValues || {};
    const query = `${CHALLAN_LIST}`;
    const bodyData = {
      ...(name && { name: name }),
    };

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setChallanList(response?.data?.data?.data);
    } else {
      message.error("Error getting challan data");
    }
  };

  const getSalesContractList = async (filterValues) => {
    const query = `${SALES_CONTRACT_LIST}`;
    const bodyData = {
      per_page: 10,
      name: filterValues?.name || "",
    };
    let response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setSalesContractList(response?.data?.data?.data);
    } else {
      message.error("Error getting sales contract data");
    }
  };

  const getBuyerList = async (name) => {
    const response = await getData(GET_BUYER_ENDPOINT, false, {
      per_page: 100,
      ...(name ? { name } : {}),
    });
    if (response?.data?.code === 200) {
      setBuyerList(response?.data?.data?.data || []);
    }
  };

  const getBuyingOfficeList = async (name) => {
    const response = await getData(GET_BUYING_OFFICE_ENDPOINT, false, {
      per_page: 100,
      ...(name ? { name } : {}),
    });
    if (response?.data?.code === 200) {
      setBuyingOfficeList(response?.data?.data?.data || []);
    }
  };

  // Effects
  useEffect(() => {
    if (!isExport) {
      setVehicleTypeList(import_vehicle_types);
      getB2BLcList();
      getSalesContractList();
    } else {
      setVehicleTypeList(import_vehicle_types);
      getBuyerList();
      getBuyingOfficeList();
    }
    getAllChallan();
  }, []);

  useEffect(() => {
    // Early return if conditions not met or update already performed
    if (
      updatePerformed ||
      !editMode ||
      !importId ||
      !poDetailsData?.length ||
      !salesContractList?.length
    ) {
      return;
    }

    // Get valid sales contract info objects from poDetailsData
    const validContractInfo = poDetailsData
      .filter((item) => item.sales_contract_info && item.sales_contract_info.id)
      .map((item) => item.sales_contract_info);

    // Create a map for quick lookups
    const contractMap = new Map();

    // Add existing sales contracts to the map
    salesContractList.forEach((contract) => {
      if (contract.id) {
        contractMap.set(contract.id, {
          id: contract.id,
          reference_no: contract.reference_no,
          sales_contract_number: contract.sales_contract_number,
        });
      }
    });

    // Add or update contracts from poDetailsData
    validContractInfo.forEach((contract) => {
      if (contract.id && !contractMap.has(contract.id)) {
        contractMap.set(contract.id, {
          id: contract.id,
          reference_no: contract.reference_no,
          sales_contract_number: contract.sales_contract_number,
        });
      }
    });

    // Convert map back to array
    const mergedOptions = Array.from(contractMap.values());

    // Only update state if there are changes
    if (
      mergedOptions.length !== salesContractList.length ||
      JSON.stringify(mergedOptions.map((c) => c.id).sort()) !==
        JSON.stringify(salesContractList.map((c) => c.id).sort())
    ) {
      setSalesContractList(mergedOptions);
    }

    // Mark update as performed
    setUpdatePerformed(true);
  }, [editMode, importId, poDetailsData, salesContractList, updatePerformed]);

  const getInvoiceList = async (lc_id, unique_id) => {
    const poDetailsDataCopy = structuredClone(poDetailsData);
    const foundPoItem = poDetailsDataCopy?.find(
      (item) => item?.unique_id === unique_id,
    );

    const response = await getData(`${lcInvoiceListApi}/${lc_id}`);

    if (response && response?.status === 200) {
      // Set invoice list and b2blc id in po data
      foundPoItem["invoice_list"] = response?.data?.data;
      foundPoItem["b2b_lc_id"] = lc_id;
    } else {
      HandleNotification("error", "bottomRight", "Something Went Wrong!", null);
    }

    setPoDetailsData(poDetailsDataCopy);
  };

  const onChangeShipmentType = async (value, unique_id) => {
    const poDetailsDataCopy = structuredClone(poDetailsData);
    const foundPoItem = poDetailsDataCopy?.find(
      (item) => item?.unique_id === unique_id,
    );
    if (!foundPoItem) return;

    foundPoItem.type_of_shipment = value;
    foundPoItem.sales_contract_id = null;
    foundPoItem.sales_contract_info = null;
    foundPoItem.b2b_lc_id = null;
    foundPoItem.import_details_id = null;
    foundPoItem.invoice_list = [];
    foundPoItem.import_qty = null;
    foundPoItem.cbm = null;
    foundPoItem.gross_weight = null;
    foundPoItem.loading_qty = null;
    foundPoItem.loading_cbm = null;

    if (value === "FOC") {
      const response = await getData(`${focInvoiceListApi}`);
      if (response && response?.data?.code === 200) {
        foundPoItem.invoice_list = response?.data?.data;
      } else {
        HandleNotification(
          "error",
          "bottomRight",
          "Something Went Wrong!",
          null,
        );
      }
    }

    setPoDetailsData(poDetailsDataCopy);
  };

  const onSelectInvoice = (id, unique_id, invoiceList) => {
    if (id === undefined || id === null || id === "") return;

    const foundInvoiceItem = (invoiceList || []).find(
      (item) => Number(item.id) === Number(id),
    );
    const poDetailsDataCopy = structuredClone(poDetailsData);
    const foundPoItem = poDetailsDataCopy?.find(
      (item) => item?.unique_id === unique_id,
    );
    if (!foundPoItem) return;

    foundPoItem.import_details_id = Number(id);
    foundPoItem.import_qty = foundInvoiceItem?.quantity_ctn
      ? Number(foundInvoiceItem.quantity_ctn)
      : 0;
    foundPoItem.cbm = foundInvoiceItem?.cbm ? Number(foundInvoiceItem.cbm) : 0;
    foundPoItem.gross_weight = foundInvoiceItem?.gross_weight
      ? Number(foundInvoiceItem.gross_weight)
      : 0;

    setPoDetailsData(poDetailsDataCopy);
  };

  const removeRow = (row_index) => {
    confirm({
      title: "Do you want to delete this row?",
      icon: <ExclamationCircleOutlined />,
      onOk() {
        const updatedPoDetailsData = poDetailsData.filter(
          (item) => item?.unique_id !== row_index,
        );
        setPoDetailsData(updatedPoDetailsData);
      },
    });
  };

  const addRow = () => {
    const lastItemUniqueId = poDetailsData[poDetailsData.length - 1]?.unique_id;
    const updatedPoDetailsData = [
      ...poDetailsData,
      { unique_id: lastItemUniqueId + 1 },
    ];
    setPoDetailsData(updatedPoDetailsData);
  };

  const onFinishVehicle = async (values) => {
    // console.log("onFinishExportAdd values", values);

    const consignmentRows = isExport
      ? poDetailsData.filter(
          (obj) =>
            obj?.buyer_id ||
            obj?.buying_office_id ||
            obj?.export_carton != null ||
            obj?.garment_piece_qty != null,
        )
      : poDetailsData.filter(
          (obj) =>
            obj?.type_of_shipment ||
            obj?.b2b_lc_id ||
            obj?.import_details_id ||
            obj?.sales_contract_id ||
            obj?.loading_qty ||
            obj?.loading_cbm,
        );

    if (isExport && !consignmentRows.length) {
      message.error("Add at least one consignment row.");
      return;
    }

    if (
      !isExport &&
      (!consignmentRows.length ||
        consignmentRows.some((obj) => !obj?.import_details_id))
    ) {
      message.error("Invoice Number is required for each LC invoice item.");
      return;
    }

    const addQuery = `${shipmentApi}`;
    const editQuery = `${shipmentApi}/${importId}`;

    const bodyData = {
      vehicle_number: values?.vehicle_number,
      transporter: values?.transporter,
      vehicle_size: values?.vehicle_size,
      vehicle_type: values?.vehicle_type,
      vehicle_qty: values?.vehicle_qty,
      empty_vehicle_arrival_time: values?.empty_vehicle_arrival_time,
      driver_name: values?.driver_name,
      driver_cell_number: values?.driver_cell_number,
      driving_license_number: values?.driving_license_number,
      driver_nid_number: values?.driver_nid_number,
      loading_place: values?.loading_place,
      dispatch_time: values?.dispatch_time,
      challan_id: values?.challan_id,
      ...(isExport && values?.gate_pass_no
        ? { gate_pass_no: values.gate_pass_no }
        : {}),
      security_bolt_seal_number: values?.security_bolt_seal_number,
      gprs_tracking_number: values?.gprs_tracking_number,
      delivery_place: values?.delivery_place,
      unloading_date: values?.unloading_date,
      arrival_date: values?.arrival_date,
      importer_name: values?.importer_name,
      ...(isExport && { shipment_by: values?.shipment_by || null }),
      ...transportChargePayload(values),
      lc_invoice_items:
        isArrayAndHasValue(consignmentRows) &&
        consignmentRows?.map((obj, index) => {
          if (isExport) {
            return {
              type_of_shipment: "Export",
              export_carton: obj?.export_carton ?? null,
              garment_piece_qty: obj?.garment_piece_qty ?? null,
              buyer_id: obj?.buyer_id || null,
              buying_office_id: obj?.buying_office_id || null,
            };
          }
          return {
            type_of_shipment: obj?.type_of_shipment || null,
            b2b_lc_id: obj?.b2b_lc_id || null,
            import_details_id: obj?.import_details_id || null,
            loading_qty: obj?.loading_qty,
            loading_cbm: obj?.loading_cbm,
            sales_contract_id: obj?.sales_contract_id || null,
          };
        }),
    };

    if (editMode) {
      const response = await putData(editQuery, bodyData);

      if (response && response?.code === 200) {
        HandleNotification(
          "success",
          "bottomRight",
          "Successfully Updated!",
          null,
        );
        navigate(listPath);
      } else {
        HandleNotification(
          "error",
          "bottomRight",
          "Something Went Wrong!",
          null,
        );
      }
    } else {
      const response = await postData(addQuery, bodyData);

      if (response && response?.code === 200) {
        HandleNotification(
          "success",
          "bottomRight",
          "Successfully Added!",
          null,
        );
        navigate(listPath);
      } else {
        HandleNotification(
          "error",
          "bottomRight",
          "Something Went Wrong!",
          null,
        );
      }
    }
  };

  const discartForm = () => {
    navigate(listPath);
  };

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Affix offsetTop={0}>
        <Card className="p-fixed">
          <Header
            title={`${
              editMode ? "Update" : "Add"
            } ${shipmentLabel} Shipment for Single Vehicle`}
            actions={[
              <Affix offsetTop={20}>
                <Button
                  size="small"
                  key={1}
                  danger
                  onClick={() => discartForm()}
                >
                  Discard
                </Button>
              </Affix>,
              <Affix offsetTop={20}>
                <Button
                  size="small"
                  key={2}
                  type="primary"
                  onClick={() => form.submit()}
                >
                  Save
                </Button>
              </Affix>,
            ]}
          />
        </Card>
      </Affix>
      <div className="containt-body pt-3">
        <Form
          layout="vertical"
          form={form}
          name="control-hooks"
          onFinish={onFinishVehicle}
        >
          <Card title={isExport ? "Factory" : "CTG Part"}>
            <Row gutter={6}>
              {isExport && (
                <Col span={4}>
                  <Form.Item
                    label="Transporter"
                    name="transporter"
                    rules={[
                      { required: true, message: "Please Select Transporter" },
                    ]}
                  >
                    <Select placeholder="Transporter" size="small">
                      {transporter.map((option) => {
                        return (
                          <option key={option.id} value={option.value}>
                            {option.value}
                          </option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              )}
              <Col span={4}>
                <Form.Item
                  label="Vehicle Number"
                  name="vehicle_number"
                  rules={[
                    { required: true, message: "Please write Vehicle Number" },
                  ]}
                >
                  <Input placeholder="Vehicle Number" size="small" />
                </Form.Item>
              </Col>
              {!isExport && (
              <Col span={4}>
                <Form.Item
                  label="Transporter"
                  name="transporter"
                  rules={[
                    { required: true, message: "Please Select Transporter" },
                  ]}
                >
                  <Select placeholder="Transporter" size="small">
                    {transporter.map((option) => {
                      return (
                        <option key={option.id} value={option.value}>
                          {option.value}
                        </option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
              )}
              <Col span={4}>
                <Form.Item
                  label="Vehicle Size"
                  name="vehicle_size"
                  rules={[
                    { required: true, message: "Please Select Vehicle Size" },
                  ]}
                >
                  <Select placeholder="Vehicle Size" size="small">
                    <option value="8 FT">8 FT</option>
                    <option value="10 FT">10 FT</option>
                    <option value="12 FT">12 FT</option>
                    <option value="14 FT">14 FT</option>
                    <option value="15 FT">15 FT</option>
                    <option value="16 FT">16 FT</option>
                    <option value="18 FT">18 FT</option>
                    <option value="20 FT">20 FT</option>
                    <option value="23 FT">23 FT</option>
                    <option value="Others">Others</option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Vehicle Type"
                  name="vehicle_type"
                  rules={[
                    { required: true, message: "Please Select Vehicle Type" },
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Vehicle Type"
                    size="small"
                    filterOption={(input, option) =>
                      String(option?.children || "")
                        .toLowerCase()
                        .includes(String(input).toLowerCase())
                    }
                  >
                    {(isExport ? import_vehicle_types : vehicleTypeList).map((type) => (
                      <Option key={type} value={type}>
                        {type}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Vehicle Quantity"
                  name="vehicle_qty"
                >
                  <InputNumber
                    placeholder="Vehicle Quantity"
                    size="small"
                    min={1}
                    precision={0}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Empty Vehicle Arrival Time"
                  name="empty_vehicle_arrival_time"
                >
                  <CustomDatePicker
                    style={{ width: "100%" }}
                    size="small"
                    showTime
                    format="DD-MMM-YYYY HH:mm:ss"
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Driver Name"
                  name="driver_name"
                  rules={[
                    { required: true, message: "Please write Driver Name" },
                  ]}
                >
                  <Input placeholder="Driver Name" size="small" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Driver Cell No."
                  name="driver_cell_number"
                  rules={[
                    { required: true, message: "Please write Driver Cell No." },
                  ]}
                >
                  <Input placeholder="Driver Cell No." size="small" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Driving License Number"
                  name="driving_license_number"
                >
                  <Input placeholder="Driving License Number" size="small" />
                </Form.Item>
              </Col>
              {/* {!isExport && (
                <Col span={4}>
                  <Form.Item label="Driver NID Number" name="driver_nid_number">
                    <Input placeholder="Driver NID Number" size="small" />
                  </Form.Item>
                </Col>
              )} */}
              <Col span={4}>
                <Form.Item
                  label="Place of Loading"
                  name="loading_place"
                >
                  <Select placeholder="Place of Loading" size="small">
                    {loadingPlaceOptions.map((option) => {
                      return (
                        <option key={option.value} value={option.value}>
                          {option.value}
                        </option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Dispatch Date Time"
                  name="dispatch_time"
                  rules={[
                    {
                      required: true,
                      message: "Please Select Dispatch Date Time",
                    },
                  ]}
                >
                  <CustomDatePicker
                    style={{ width: "100%" }}
                    size="small"
                    showTime
                    format="DD-MMM-YYYY HH:mm:ss"
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <div style={{ display: "flex", width: "100%" }}>
                  <div style={{ width: "80%", paddingRight: "4px" }}>
                    <Form.Item
                      label="Challan No."
                      name="challan_id"
                      rules={[
                        {
                          required: true,
                          message: `Please select Challan No.`,
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder="Search to Select"
                        optionFilterProp="children"
                        allowClear
                        onClear={() => {
                          form.setFieldsValue({ challan_id: null });
                        }}
                        size="small"
                        onSearch={(value) => getAllChallan({ name: value })}
                        filterOption={false}
                      >
                        {isArrayAndHasValue(challanList) &&
                          challanList?.map((challanItem, index) => (
                            <Option
                              key={challanItem?.id || index}
                              value={challanItem?.id}
                            >
                              {challanItem?.challan_no || ""}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </div>
                  <div style={{ width: "20%" }}>
                    <Form.Item label=" ">
                      <Button
                        icon={<PlusOutlined />}
                        size="small"
                        onClick={onModalOpen}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </div>
                </div>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Security Bolt Seal No."
                  name="security_bolt_seal_number"
                  rules={[
                    {
                      required: true,
                      message: "Please write Security Bolt Seal No.",
                    },
                  ]}
                >
                  <Input placeholder="Security Bolt seal number" size="small" />
                </Form.Item>
              </Col>
              {/* {!isExport && (
                <Col span={4}>
                  <Form.Item
                    label="GPRS Tracking Number"
                    name="gprs_tracking_number"
                  >
                    <Input placeholder="GPRS Tracking Number" size="small" />
                  </Form.Item>
                </Col>
              )} */}
              <Col span={4}>
                <Form.Item
                  label="Place of Arrival"
                  name="delivery_place"
                  rules={[
                    {
                      required: true,
                      message: "Please Select Place of Arrival",
                    },
                  ]}
                >
                  <Select placeholder="Place of Arrival" size="small">
                    {arrivalPlaceOptions.map((option) => {
                      return (
                        <option key={option.value} value={option.value}>
                          {option.value}
                        </option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label={`${shipmentLabel}er Name`}
                  name="importer_name"
                  rules={[
                    {
                      required: true,
                      message: `Please Select ${shipmentLabel}er Name`,
                    },
                  ]}
                >
                  <Select placeholder={`${shipmentLabel}er Name`} size="small">
                    {importerNameOptions.map((name) => (
                      <Option key={name} value={name}>
                        {name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {isExport && (
                <Col span={4}>
                  <Form.Item label="Shipment By" name="shipment_by">
                    <Input placeholder="Shipment By" size="small" />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Card>

          {isExport && (
            <Card title="CTG" style={{ marginTop: 16 }}>
              <Row gutter={6}>
                <Col span={4}>
                  <Form.Item label="Arrival Date & Time" name="arrival_date">
                    <CustomDatePicker
                      style={{ width: "100%" }}
                      size="small"
                      showTime
                      format="DD-MMM-YYYY HH:mm:ss"
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Unloading Date Time" name="unloading_date">
                    <CustomDatePicker
                      style={{ width: "100%" }}
                      size="small"
                      showTime
                      format="DD-MMM-YYYY HH:mm:ss"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          {!isExport && (
            <Card title="Factory" style={{ marginTop: 16 }}>
              <Row gutter={6}>
                <Col span={4}>
                  <Form.Item label="Arrival Date & Time" name="arrival_date">
                    <CustomDatePicker
                      style={{ width: "100%" }}
                      size="small"
                      showTime
                      format="DD-MMM-YYYY HH:mm:ss"
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Unloading Date Time" name="unloading_date">
                    <CustomDatePicker
                      style={{ width: "100%" }}
                      size="small"
                      showTime
                      format="DD-MMM-YYYY HH:mm:ss"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          <Card title="Consignment Details" style={{ marginTop: 16 }}>
          <div className="custome-table" style={{ maxWidth: "100%" }}>
            <table border="1" style={{ marginBottom: "30px", width: "100%" }}>
              <thead>
                <tr>
                  <th key="header_shipment_type" className="bc-java ">
                    Type of Shipment
                  </th>
                  {isExport ? (
                    <>
                      <th key="header_buyer" className="bc-java ">
                        Buyer
                      </th>
                      <th key="header_buying_office" className="bc-java ">
                        Buying Office
                      </th>
                      <th key="header_export_carton" className="bc-java ">
                        Export Carton
                      </th>
                      <th key="header_garment_piece_qty" className="bc-java ">
                        Garment Piece Quantity
                      </th>
                    </>
                  ) : (
                    <>
                      <th key="header_sales_contract" className="bc-java ">
                        Sales Contract
                      </th>
                      <th key="header_lc_number" className="bc-java ">
                        LC No.
                      </th>
                      <th key="header_invoice_number" className="bc-java ">
                        Invoice No.
                      </th>
                      <th key="header_import_qty" className="bc-java ">
                        {shipmentLabel} Qty.
                      </th>
                      <th key="header_cbm" className="bc-java ">
                        CBM
                      </th>
                      <th key="header_gross_weight" className="bc-java ">
                        Gross Weight
                      </th>
                      <th key="header_loading_qty" className="bc-java ">
                        Loading Qty.
                      </th>
                      <th key="header_loading_cbm" className="bc-java ">
                        Loading CBM
                      </th>
                    </>
                  )}
                  <th
                    key="header_add_row"
                    className="bc-java "
                    style={{
                      width: 42,
                      minWidth: 42,
                      maxWidth: 42,
                      padding: "4px 0",
                      letterSpacing: 0,
                      textTransform: "none",
                      fontSize: 12,
                      lineHeight: "14px",
                      whiteSpace: "nowrap",
                      overflow: "visible",
                    }}
                  >
                    Add
                  </th>
                  <th
                    key="header_delete_row"
                    className="bc-java "
                    style={{
                      width: 52,
                      minWidth: 52,
                      maxWidth: 52,
                      padding: "4px 0",
                      letterSpacing: 0,
                      textTransform: "none",
                      fontSize: 12,
                      lineHeight: "14px",
                      whiteSpace: "nowrap",
                      overflow: "visible",
                    }}
                  >
                    Delete
                  </th>
                </tr>
              </thead>

              <tbody className="scrollable-table-container">
                {isArrayAndHasValue(poDetailsData) &&
                  poDetailsData?.map((obj, index) => {
                    return (
                      <tr key={`table_parent_key_${index}`}>
                        {isExport ? (
                          <>
                            <td className="t-body sticky">
                              <Select
                                style={{ width: "100%" }}
                                size="small"
                                value="Export"
                                disabled
                              >
                                <Option value="Export">Export</Option>
                              </Select>
                            </td>
                            <td className="t-body sticky">
                              <Select
                                placeholder="Buyer"
                                style={{ width: "100%" }}
                                showSearch
                                allowClear
                                size="small"
                                optionFilterProp="children"
                                onSearch={(value) => getBuyerList(value)}
                                onChange={(value) =>
                                  onChangePoDetailsValue(
                                    value,
                                    obj?.unique_id,
                                    "buyer_id",
                                  )
                                }
                                value={obj?.buyer_id}
                              >
                                {buyerList.map((buyer) => (
                                  <Option key={buyer.id} value={buyer.id}>
                                    {buyer.name}
                                  </Option>
                                ))}
                              </Select>
                            </td>
                            <td className="t-body sticky">
                              <Select
                                placeholder="Buying Office"
                                style={{ width: "100%" }}
                                showSearch
                                allowClear
                                size="small"
                                optionFilterProp="children"
                                onSearch={(value) => getBuyingOfficeList(value)}
                                onChange={(value) =>
                                  onChangePoDetailsValue(
                                    value,
                                    obj?.unique_id,
                                    "buying_office_id",
                                  )
                                }
                                value={obj?.buying_office_id}
                              >
                                {buyingOfficeList.map((office) => (
                                  <Option key={office.id} value={office.id}>
                                    {office.name}
                                  </Option>
                                ))}
                              </Select>
                            </td>
                            <td className="t-body sticky">
                              <InputNumber
                                onChange={(value) =>
                                  onChangePoDetailsValue(
                                    value,
                                    obj?.unique_id,
                                    "export_carton",
                                  )
                                }
                                placeholder="Export Carton"
                                size="small"
                                min={0}
                                precision={0}
                                style={{ width: "100%" }}
                                value={obj?.export_carton}
                              />
                            </td>
                            <td className="t-body sticky">
                              <InputNumber
                                onChange={(value) =>
                                  onChangePoDetailsValue(
                                    value,
                                    obj?.unique_id,
                                    "garment_piece_qty",
                                  )
                                }
                                placeholder="Garment Piece Quantity"
                                size="small"
                                min={0}
                                precision={0}
                                style={{ width: "100%" }}
                                value={obj?.garment_piece_qty}
                              />
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="t-body sticky">
                              <Select
                                placeholder="Type"
                                style={{ width: "100%" }}
                                showSearch
                                size="small"
                                onChange={(value) =>
                                  onChangeShipmentType(value, obj?.unique_id)
                                }
                                value={obj?.type_of_shipment}
                              >
                                <option value="LC">LC</option>
                                <option value="RTGS">RTGS</option>
                                <option value="FOC">FOC</option>
                                <option value="TT">TT</option>
                              </Select>
                            </td>
                            <td className="t-body sticky">
                              <Select
                                placeholder="Sales Contract"
                                style={{ width: "100%" }}
                                showSearch
                                size="small"
                                onChange={(value) =>
                                  onChangePoDetailsValue(
                                    value,
                                    obj?.unique_id,
                                    "sales_contract_id",
                                  )
                                }
                                value={obj?.sales_contract_id}
                                disabled={obj?.type_of_shipment !== "FOC"}
                              >
                                {isArrayAndHasValue(salesContractList) &&
                                  salesContractList.map((option) => {
                                    return (
                                      <Select.Option
                                        key={option.id}
                                        value={option.id}
                                      >
                                        {option.reference_no}
                                      </Select.Option>
                                    );
                                  })}
                              </Select>
                            </td>
                            <td className="t-body sticky">
                              <Select
                                placeholder="LC Number"
                                style={{ width: "100%" }}
                                showSearch
                                filterOption={(input, option) =>
                                  option.children
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                size="small"
                                onSelect={(value) =>
                                  getInvoiceList(value, obj?.unique_id)
                                }
                                value={obj?.b2b_lc_id}
                                disabled={obj?.type_of_shipment === "FOC"}
                              >
                                {b2bLcList.map((option) => {
                                  return (
                                    <option key={option.id} value={option.id}>
                                      {option.b2b_lc_number}
                                    </option>
                                  );
                                })}
                              </Select>
                            </td>
                            <td className="t-body sticky">
                              <Select
                                placeholder="Invoice No."
                                style={{ width: "100%" }}
                                showSearch
                                filterOption={(input, option) =>
                                  option.children
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                size="small"
                                onChange={(value) =>
                                  onSelectInvoice(
                                    value,
                                    obj?.unique_id,
                                    obj?.invoice_list,
                                  )
                                }
                                disabled={!isArrayAndHasValue(obj?.invoice_list)}
                                value={obj?.import_details_id}
                              >
                                {isArrayAndHasValue(obj?.invoice_list) &&
                                  obj?.invoice_list?.map((option) => {
                                    return (
                                      <option key={option.id} value={option.id}>
                                        {option.invoice_number}
                                      </option>
                                    );
                                  })}
                              </Select>
                            </td>
                            <td className="t-body sticky">
                              <Input
                                value={obj?.import_qty}
                                placeholder={`${shipmentLabel} Qty.`}
                                size="small"
                                disabled
                              />
                            </td>
                            <td className="t-body sticky">
                              <Input
                                value={
                                  obj?.cbm != null && !isNaN(Number(obj.cbm))
                                    ? Number(obj.cbm).toFixed(2)
                                    : ""
                                }
                                placeholder="CBM"
                                size="small"
                                disabled
                              />
                            </td>
                            <td className="t-body sticky">
                              <Input
                                value={
                                  obj?.gross_weight != null &&
                                  !isNaN(Number(obj.gross_weight))
                                    ? Number(obj.gross_weight).toFixed(2)
                                    : ""
                                }
                                placeholder="Gross Weight"
                                size="small"
                                disabled
                              />
                            </td>
                            <td className="t-body sticky">
                              <InputNumber
                                onChange={(value) =>
                                  onChangePoDetailsValue(
                                    value,
                                    obj?.unique_id,
                                    "loading_qty",
                                  )
                                }
                                placeholder="Loading Qty."
                                size="small"
                                style={{ width: "100%" }}
                                value={obj?.loading_qty}
                              />
                            </td>
                            <td className="t-body sticky">
                              <InputNumber
                                onChange={(value) =>
                                  onChangePoDetailsValue(
                                    value,
                                    obj?.unique_id,
                                    "loading_cbm",
                                  )
                                }
                                placeholder="Loading CBM"
                                size="small"
                                style={{ width: "100%" }}
                                value={obj?.loading_cbm}
                              />
                            </td>
                          </>
                        )}
                        <td
                          className="t-body"
                          onClick={addRow}
                          style={{
                            width: 42,
                            minWidth: 42,
                            maxWidth: 42,
                            padding: "4px 0",
                            textAlign: "center",
                            overflow: "visible",
                          }}
                        >
                          <PlusCircleOutlined
                            className="cursore-pointer"
                            style={{ fontSize: 20, color: "green", verticalAlign: "middle" }}
                          />
                        </td>
                        <td
                          className="t-body"
                          onClick={() => {
                            removeRow(obj?.unique_id);
                          }}
                          style={{
                            width: 52,
                            minWidth: 52,
                            maxWidth: 52,
                            padding: "4px 0",
                            textAlign: "center",
                            overflow: "visible",
                          }}
                        >
                          <MinusCircleOutlined
                            className="cursore-pointer"
                            style={{ fontSize: 20, color: "red", verticalAlign: "middle" }}
                          />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          </Card>

          <ShipmentTransportCharges
            isExport={isExport}
            vehicleCardTitle="Transporter Details"
            hideVehicleNumber
          />
        </Form>
        <Modal
          open={isModalOpen}
          title={"Create Challan"}
          onCancel={() => {
            onCloseModal();
          }}
          maskStyle={{ backdropFilter: "blur(4px)" }}
          footer={null}
        >
          <ChallanForm
            isEdit={false}
            onCloseModal={onCloseModal}
            from={"inventory"}
            refetch={getAllChallan}
            isVehicleStatus={true}
          />
        </Modal>
      </div>
    </>
  );
});

export default GoodsDeliveryForm;
